import { AUTH_TOKEN_EXPIRED } from "wombat-global/src/utilities/error";
import { hasAuthErrorCode, isSessionAuthError } from "../utilities/auth-errors";
import { loginRecovery } from "../utilities/login-recovery";

// for vite build
const env = import.meta.env;

// for CRA build
// const env = process.env;

export class Request {
  private baseUrl?: string;
  private refreshTokenHandler?: () => Promise<string>;

  constructor(props: { headers?: Headers; baseUrl: string }) {
    if (props.baseUrl) {
      this.baseUrl = props.baseUrl;
    }
  }

  public setRefreshHandler(refreshHandler?: () => Promise<string>): void {
    this.refreshTokenHandler = refreshHandler;
  }

  private async fetch(url: string, options: RequestInit = {}) {
    const accessToken = await this.refreshTokenHandler?.().catch(
      async (err: unknown) => {
        if (isSessionAuthError(err)) {
          await loginRecovery();
        }
        throw err;
      },
    );
    const headers = new Headers(options.headers);
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    // set default headers
    headers.set("Content-Type", "application/json");
    headers.set("Accept", "application/json");

    const requestOptions = Object.assign({}, options, { headers });
    const fetchUrl = this.baseUrl ? `${this.baseUrl}${url}` : url;
    return fetch(fetchUrl, requestOptions);
  }

  private tryParseJSON(text: string) {
    try {
      return JSON.parse(text);
    } catch (err) {
      return undefined;
    }
  }

  public async request<T = JSON | string>(
    url: string,
    options: RequestInit = {},
  ): Promise<[Response, T]> {
    const response = await this.fetch(url, options);

    const bodyText = await response.text();
    let bodyJson = undefined;
    if (response.headers.get("Content-Type")?.includes("application/json")) {
      bodyJson = this.tryParseJSON(bodyText);
    }

    if (!response.ok) {
      if (
        response.status === 401 &&
        hasAuthErrorCode(bodyJson, AUTH_TOKEN_EXPIRED)
      ) {
        await loginRecovery();
      }
      throw bodyJson || bodyText;
    } else {
      return [response, bodyJson || bodyText];
    }
  }
}

export default new Request({
  baseUrl: `${env.REACT_APP_API_URL}`,
});
